import {
  ActionMenuItem,
  Caption,
  ControlledNumberField,
  ControlledSelect,
  DisplayValue,
  FieldsetCard,
  FormContainer,
  FormSetup,
  OverlineCardHeader,
  PageContainer,
  SplitContainer,
  useTranslatedOptionEnum,
} from '@if/mui'
import { EFinancingProgram, EPaymentFrequency, EProvince, getEProvinceList } from '@if/core/types'
import { formatCurrency, formatDate, formatPercentage } from '@if/core/utils'

import { Card, CardContent, Stack } from '@mui/material'
import { useComputeProductsLoan } from '@src/data/api/calculator-api/calculator-api'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { formatComputed } from '@src/data/worksheet-selectors'
import { CalculatorSchema, CalculatorSchemaType } from '@src/types/calculatorSchema'
import { ProductsComputeRequestDto } from '@src/types/ProductsComputeRequestDto'
import { ProductsComputeResultDto } from '@src/types/ProductsComputeResultDto'
import { getFirstPaymentOnMinMax } from '@src/types/WorksheetSchema'
import { memo, useCallback, useMemo, useState } from 'react'
import { useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// Module-scope so the reference is stable across renders (captures no props/state).
const useFormSetup = (): FormSetup<typeof CalculatorSchema> => ({ schema: CalculatorSchema })

// Inside the form so it can read its errors: the last result stays in state while a field is invalid.
const ProductsPaymentCard = ({ calculatedRate }: { calculatedRate: ProductsComputeResultDto | null }) => {
  const { t } = useTranslation()
  const { errors } = useFormState()
  const hasErrors = Object.keys(errors).length > 0
  const result = hasErrors ? null : calculatedRate
  const showAnnualFees = (result?.annualFees ?? 0) > 0

  return (
    <Card>
      <OverlineCardHeader title={t('calculator.monthlyPayment')} />
      <CardContent>
        <Stack spacing={1}>
          <DisplayValue>
            {formatComputed(hasErrors ? null : (calculatedRate?.paymentAmountForFrequency ?? 0), formatCurrency)}
          </DisplayValue>
          {result && (
            <>
              <Caption>
                {t('calculator.totalInterestCosts', {
                  rate: formatPercentage(result.interestRate),
                })}
              </Caption>
              <Caption>{t('calculator.effectiveRate', { effectiveRate: result.effectiveRate })}</Caption>
              {showAnnualFees && <Caption>{t('calculator.annualFees', { annualFees: result.annualFees })}</Caption>}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

const ProductsCalculator = () => {
  const { t } = useTranslation()

  const [calculatorData, setCalculatorData] = useState<ProductsComputeRequestDto | undefined>(undefined)
  const [calculatedRate] = useComputeProductsLoan(calculatorData)

  const programConfig = programConfigByFinancingProgramId(EFinancingProgram.Products)
  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const paymentFrequencyOptions = useTranslatedOptionEnum('EPaymentFrequency', programConfig.frequencyPayments, 'enum')
  const termsOptions = useMemo(
    () =>
      programConfig.termList.map((value: number) => (
        <ActionMenuItem key={value} value={value}>{`${value} ${t('common.months')}`}</ActionMenuItem>
      )),
    [t, programConfig],
  )

  // Compute-on-submit, driven by the filters form: any field change auto-submits
  // (debounced), feeding onSubmit's request into the aside card. No persistence.
  const handleSubmit = useCallback((data: CalculatorSchemaType) => {
    const deliveryOn = formatDate(new Date())
    setCalculatorData({
      stateIso: data.stateIso ?? EProvince.Nunavut,
      amountRequested: data.amountRequested ?? 0,
      frequency: data.frequency!,
      term: data.terms!,
      deliveryOn,
      firstPaymentDate: formatDate(getFirstPaymentOnMinMax(deliveryOn, data.frequency ?? EPaymentFrequency.Monthly)[0]),
    })
  }, [])

  return (
    <PageContainer
      title={t('calculator.productsCalculatorTitle')}
      subtitle={t('calculator.productsCalculatorSubtitle')}
    >
      <FormContainer mode="filters" useFormSetup={useFormSetup} onSubmit={handleSubmit} hideActions>
        {({ control }) => (
          <SplitContainer
            main={
              <FieldsetCard title={t('calculator.loanDetails')}>
                <Stack spacing={2}>
                  <ControlledSelect
                    name="stateIso"
                    control={control}
                    options={provinceOptions}
                    label={t('calculator.province')}
                    disableEmptyOption
                    fullWidth
                  />
                  <ControlledSelect
                    name="frequency"
                    control={control}
                    options={paymentFrequencyOptions}
                    label={t('calculator.frequency.label')}
                    disableEmptyOption
                    fullWidth
                  />
                  <ControlledSelect
                    name="terms"
                    control={control}
                    options={termsOptions}
                    label={t('calculator.terms')}
                    disableEmptyOption
                    fullWidth
                  />
                  <ControlledNumberField
                    name="amountRequested"
                    control={control}
                    label={t('calculator.amountRequested')}
                    isMoney
                    fullWidth
                  />
                </Stack>
              </FieldsetCard>
            }
            aside={<ProductsPaymentCard calculatedRate={calculatedRate} />}
          />
        )}
      </FormContainer>
    </PageContainer>
  )
}

export default memo(ProductsCalculator)
